import { useEffect, useRef, useState } from "react"
import { Box } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { AudienceAwareAmendsEditor } from "./audience-aware-amends-editor"
import { SeedSource } from "authoring/lib/enum/seed-source.mjs"
import { useScheduleEditorSeed } from "../../schedule-editor/hooks/use-schedule-editor-seed"

export function AmendsBoundaryEditor({
    amendsKey,
    subtitle,
    alwaysOpen = false,
    autoFocusOnOpen = true,
    allowSplit = true,
    isCollapsed,
    onToggleCollapsed,
    collapsible = false,
    required = true,
    ...props
}) {
    const { amendsContext, macroMap } = useBoundContext()
    const { seedSource } = useScheduleEditorSeed()
    const [busy, setBusy] = useState(false)
    const busyRef = useRef(false)
    if (!amendsContext) {
        throw new Error("AmendsBoundaryEditor requires amendsContext")
    }

    const amendsState = amendsContext.getAmendsState(amendsKey)
    const { isOpen, isEditMode, canEdit, authorName, savedAt, hasSavedAmends } = amendsState
    const showDelete = Boolean(canEdit && isEditMode && hasSavedAmends)
    useEffect(ensureAlwaysOpen, [alwaysOpen, isOpen, amendsKey, amendsContext])

    if (!isOpen) return null

    return (
        <Box mt={1.5} data-cy="draft-amends-editor-field" {...props}>
            <AudienceAwareAmendsEditor
                macroMap={macroMap}
                editMode={isEditMode}
                allowSplit={allowSplit}
                noteData={amendsState.noteData}
                subtitle={subtitle}
                required={required && seedSource !== SeedSource.NEW}
                onChange={handleChange}
                onCancel={cancel}
                onSave={save}
                onEdit={edit}
                onDelete={remove}
                showDelete={showDelete}
                canEdit={canEdit}
                authorName={authorName}
                savedAt={savedAt}
                busy={busy}
                autoFocusOnOpen={autoFocusOnOpen}
                isCollapsed={isCollapsed}
                onToggleCollapsed={onToggleCollapsed}
                collapsible={collapsible}
            />
        </Box>
    )

    function handleChange(value) {
        amendsContext.onChangeAmends(amendsKey, value)
    }

    function ensureAlwaysOpen() {
        if (!alwaysOpen) return
        if (isOpen) return
        amendsContext.onOpenAmends(amendsKey)
    }

    function cancel() {
        amendsContext.onCancelAmends(amendsKey)
    }

    async function save() {
        if (busyRef.current) return null

        busyRef.current = true
        setBusy(true)
        try {
            return await amendsContext.onSaveAmends(amendsKey)
        } finally {
            busyRef.current = false
            setBusy(false)
        }
    }

    function edit() {
        amendsContext.onEditAmends(amendsKey)
    }

    async function remove() {
        if (busyRef.current) return null

        busyRef.current = true
        setBusy(true)
        try {
            return await amendsContext.onDeleteAmends(amendsKey)
        } finally {
            busyRef.current = false
            setBusy(false)
        }
    }
}
